import { getEngine } from "../../engines/engineRegistry";
import { applyFoundationAssembly, collectFoundationAssembly, type AssemblyPart } from "./foundationAssembly";
import { DEFAULT_FINISHES } from "../../domain/finishes/types";
import { useFrame, useThree } from "@react-three/fiber";
import { useCallback, useEffect, useMemo, useRef } from "react";
import type * as THREE from "three";
import {
	ALL_DIRTY,
	computeDirtyFlags,
	DirtyFlag,
} from "../../engines/dirtyTracking";
import {
	type FrontVisibilityGroups,
	type FrontVisibilityLayer,
	getFrontVisibilityGroup,
	applyDividerVisibility,
} from "../../engines/frontVisibility";
import {
	applyGlassQualityMode,
	applyFinishSelection,
	applyVentilationDiagramMode,
	applyWoodSurfaceQualityMode,
} from "../../engines/materials";
import type {
	BuildInput,
	BuildResult,
	SaunaEngine,
	VisibilityFlags,
} from "../../engines/types";
import type { BuildStepView } from "./buildStepView";
import {
	animateHeaterAmbienceFrame,
	collectHeaterAnimationTargets,
	createEmptyHeaterAnimationTargets,
	type HeaterAnimationTargets,
	resetHeaterAnimationTargets,
} from "./heaterAmbience";
import { applyInteractionVisibility, createInteractionOverlays } from "./interactionOverlays";
import { syncOpeningGuides } from "./openingGuides";
import {
	playRevealAnimation,
	type RevealGroupEntry,
	shouldPlayRevealAnimation,
} from "./revealAnimation";
import {
	disposeGroupGeometries,
	disposeInteractionGroup,
} from "./sceneDisposal";
import {
	applyBuildResultVisibility,
	applyCeilingFinishVisibility,
	applyCeilingStackVisibility,
	applySceneGroupVisibility,
	type SceneGroupRefs,
	type SceneVisibility,
} from "./sceneVisibility";
import { measureShadowCasterBounds } from "./sunShadowFrustum";
import type { TimeOfDay, ViewerRuntimeProfile } from "./viewerConfig";
import { premiumModulesEnabled } from "./viewerConfig";
import {
	attachPremiumSlots,
	loadPremiumModuleSource,
} from "./viewerPremiumModules";

interface SaunaModelProps {
	buildInput: BuildInput;
	visibility: SceneVisibility;
	engine: SaunaEngine;
	runtimeProfile: ViewerRuntimeProfile;
	timeOfDay: TimeOfDay;
	showOpeningGuides: boolean;
	stepView?: BuildStepView | null;
	foundationProgress?: number;
	assemblyReducedMotion?: boolean;
	/**
	 * Object the built sauna group is attached to. The viewer passes the
	 * rotated "sauna rig" so a whole-building rotation is one transform on a
	 * parent rather than something every consumer has to re-derive. Falls back
	 * to the scene root when absent (tests, any non-rotating host).
	 */
	parentRef?: React.RefObject<THREE.Object3D | null>;
	/**
	 * Fired after every dirty-tracked rebuild, carrying the world bounds
	 * of the shadow casters so the sun's shadow camera can be re-fitted
	 * without a per-frame measurement (issue 121).
	 */
	onSceneChanged?: (bounds: THREE.Box3 | null) => void;
}

// ─── Premium Asset Loader ────────────────────────────────────

function resolvePremiumTargetGroup(
	groups: FrontVisibilityGroups,
	targetGroup: FrontVisibilityLayer,
) {
	return groups[targetGroup];
}

function loadPremiumAssets(
	result: BuildResult,
	loadVersion: number,
	premiumLoadVersionRef: React.MutableRefObject<number>,
	saunaGroupRef: React.MutableRefObject<THREE.Group | null>,
	onAssetLoaded: (premiumObject: THREE.Object3D) => void,
) {
	if (!premiumModulesEnabled || result.premiumPlacements.length === 0) return;

	for (const placement of result.premiumPlacements) {
		const targetGroup = resolvePremiumTargetGroup(
			result.frontVisibilityGroups,
			placement.targetGroup,
		);
		if (!targetGroup) continue;

		loadPremiumModuleSource(placement.assetPath)
			.then((source) => {
				if (
					premiumLoadVersionRef.current !== loadVersion ||
					saunaGroupRef.current !== result.saunaGroup
				) {
					return;
				}

				const fallbackObject = result.saunaGroup.getObjectByName(
					placement.fallbackObjectName,
				);
				if (!fallbackObject) return;

				const premiumObject = source.clone(true);
				premiumObject.name = `detail/premium/${placement.id}/${placement.openingId}`;
				premiumObject.position.copy(placement.position);
				premiumObject.rotation.copy(placement.rotation);
				premiumObject.scale.copy(placement.scale);
				targetGroup.add(premiumObject);
				fallbackObject.visible = false;
				onAssetLoaded(premiumObject);
			})
			.catch(() => {
				// Keep procedural fallback visible
			});
	}
}

// ─── SaunaModel Component ────────────────────────────────────

export function SaunaModel({
	buildInput,
	visibility,
	engine,
	runtimeProfile,
	timeOfDay,
	showOpeningGuides,
	stepView = null,
	foundationProgress,
	assemblyReducedMotion = false,
	parentRef,
	onSceneChanged,
}: SaunaModelProps) {
	const scene = useThree((s) => s.scene);
	const invalidate = useThree((s) => s.invalidate);
	// Resolved at mutation time, never at render time: the rig's ref is
	// populated during commit, and every rebuild here runs from an effect or
	// an rAF callback, so it is always live by then.
	const parentRefStable = useRef(parentRef);
	parentRefStable.current = parentRef;
	const resolveParent = useCallback((): THREE.Object3D => {
		const candidate = parentRefStable.current?.current;
		// `isObject3D` rather than a truthiness check: a host that renders this
		// component outside a real R3F tree (component tests mock the reconciler,
		// so `<group ref>` resolves to a DOM node) would otherwise hand us
		// something with no `.add` and take the whole scene down.
		return candidate?.isObject3D ? candidate : scene;
	}, [scene]);

	const saunaGroupRef = useRef<THREE.Group | null>(null);
	const groupRefs = useRef<SceneGroupRefs>({});
	const assemblyRef = useRef<{
		root: THREE.Group | undefined;
		parts: AssemblyPart[];
		progress?: number;
		enabled?: boolean;
		reducedMotion?: boolean;
	}>({ root: undefined, parts: [] });
	const foundationProgressRef = useRef(foundationProgress);
	foundationProgressRef.current = foundationProgress;
	useEffect(() => {
		invalidate();
	}, [foundationProgress, assemblyReducedMotion, invalidate]);
	useEffect(() => () => {
		applyFoundationAssembly(assemblyRef.current.parts, 1);
	}, []);
	const interactionGroupRef = useRef<THREE.Group | null>(null);
	const frontVisibilityRefs = useRef<FrontVisibilityGroups>({});
	const prevBuildInputRef = useRef<BuildInput | null>(null);
	const prevEngineRef = useRef<SaunaEngine | null>(null);
	const premiumLoadVersionRef = useRef(0);
	const visibilityRef = useRef(visibility);
	const appliedVisibilityRef = useRef<VisibilityFlags | null>(null);
	const heaterAnimationTargetsRef = useRef<HeaterAnimationTargets>(
		createEmptyHeaterAnimationTargets(),
	);
	const cancelRevealRef = useRef<(() => void) | null>(null);
	const previousStepViewTargetRef = useRef<string | null>(null);
	const onSceneChangedRef = useRef(onSceneChanged);
	onSceneChangedRef.current = onSceneChanged;
	const notifySceneChanged = useCallback(() => {
		onSceneChangedRef.current?.(
			measureShadowCasterBounds(saunaGroupRef.current),
		);
	}, []);

	// Keep refs fresh without triggering rebuilds
	const buildInputRef = useRef(buildInput);
	buildInputRef.current = buildInput;
	const showOpeningGuidesRef = useRef(showOpeningGuides);
	showOpeningGuidesRef.current = showOpeningGuides;
	const engineRef = useRef(engine);
	engineRef.current = engine;
	// Read by the rebuild callbacks so a tier flip between renders
	// can't bake stale material quality into a fresh build.
	const physicalGlassEnabled = runtimeProfile.physicalGlassEnabled;
	const physicalGlassEnabledRef = useRef(physicalGlassEnabled);
	physicalGlassEnabledRef.current = physicalGlassEnabled;
	const physicalWoodSurfacesEnabledRef = useRef(
		runtimeProfile.physicalWoodSurfacesEnabled,
	);
	physicalWoodSurfacesEnabledRef.current =
		runtimeProfile.physicalWoodSurfacesEnabled;

	useEffect(() => {
		visibilityRef.current = visibility;
	}, [visibility]);

	useEffect(
		() => () => {
			premiumLoadVersionRef.current += 1;
			cancelRevealRef.current?.();
			cancelRevealRef.current = null;
			if (saunaGroupRef.current) {
				disposeGroupGeometries(saunaGroupRef.current);
				// Detach from whichever parent it was attached to (scene root or
				// the rotated rig) — `scene.remove` is a no-op on a nested child.
				saunaGroupRef.current.removeFromParent();
				saunaGroupRef.current = null;
			}
		},
		[],
	);

	const flagToGroupKey: Record<DirtyFlag, keyof typeof groupRefs.current> =
		useMemo(
			() => ({
				[DirtyFlag.Foundation]: "foundation",
				[DirtyFlag.Deck]: "deck",
				[DirtyFlag.Framing]: "framing",
				[DirtyFlag.Panel]: "panel",
				[DirtyFlag.Roof]: "roof",
				[DirtyFlag.Interior]: "interior",
				[DirtyFlag.Exterior]: "exterior",
			}),
			[],
		);

	const flagToVisKey: Record<DirtyFlag, keyof VisibilityFlags> = useMemo(
		() => ({
			[DirtyFlag.Foundation]: "foundation",
			[DirtyFlag.Deck]: "deck",
			[DirtyFlag.Framing]: "framing",
			[DirtyFlag.Panel]: "panel",
			[DirtyFlag.Roof]: "roof",
			[DirtyFlag.Interior]: "interior",
			[DirtyFlag.Exterior]: "exterior",
		}),
		[],
	);

	const refreshHeaterAnimationTargets = useCallback(
		(root: THREE.Object3D | null) => {
			const nextTargets = collectHeaterAnimationTargets(root);
			heaterAnimationTargetsRef.current = nextTargets;
			resetHeaterAnimationTargets(nextTargets, timeOfDay, runtimeProfile);
		},
		[runtimeProfile, timeOfDay],
	);

	const onPremiumAssetLoaded = useCallback(
		(premiumObject: THREE.Object3D) => {
			// The asset arrives after the initial material swap. Read the
			// current tier in case it changed while the download was pending.
			applyGlassQualityMode(premiumObject, physicalGlassEnabledRef.current);
			notifySceneChanged();
			invalidate();
		},
		[invalidate, notifySceneChanged],
	);

	// Premium slots travel with the objects, so this runs after every
	// rebuild — a subsystem rebuild brings fresh procedural slots too.
	const attachSlotAssets = useCallback(
		(root: THREE.Object3D) => {
			if (!premiumModulesEnabled) return;
			attachPremiumSlots(
				root,
				(current) => saunaGroupRef.current === current,
				onPremiumAssetLoaded,
			);
		},
		[onPremiumAssetLoaded],
	);

	// ── Stable rebuild functions that read from refs ──────────
	// These don't depend on buildInput/engine, so they maintain
	// stable identities and won't trigger redundant useEffect runs.

	const fullRebuild = useCallback(() => {
		const currentBuildInput = buildInputRef.current;
		const currentEngine = engineRef.current;
		const isFirstBuild = prevBuildInputRef.current === null;

		// Settle any in-flight reveal before the groups it animates
		// are disposed.
		cancelRevealRef.current?.();
		cancelRevealRef.current = null;

		// Remove old sauna group from scene and free GPU geometry buffers
		if (saunaGroupRef.current) {
			disposeGroupGeometries(saunaGroupRef.current);
			saunaGroupRef.current.removeFromParent();
			saunaGroupRef.current = null;
		}
		premiumLoadVersionRef.current += 1;
		const loadVersion = premiumLoadVersionRef.current;

		const result = currentEngine.buildSauna3D(currentBuildInput);
		syncOpeningGuides(
			result.framingGroup,
			currentBuildInput,
			showOpeningGuidesRef.current,
		);
		result.saunaGroup.name ||= "sauna/root";
		resolveParent().add(result.saunaGroup);
		saunaGroupRef.current = result.saunaGroup;

		groupRefs.current = {
			foundation: result.foundationGroup,
			deck: result.deckGroup,
			framing: result.framingGroup,
			panel: result.panelGroup,
			roof: result.roofGroup,
			interior: result.interiorGroup,
			exterior: result.exteriorGroup,
		};
		frontVisibilityRefs.current = result.frontVisibilityGroups;

		const interactionGroup = createInteractionOverlays(currentBuildInput);
		result.saunaGroup.add(interactionGroup);
		interactionGroupRef.current = interactionGroup;

		const vis = visibilityRef.current;
		applyBuildResultVisibility(result, vis);
		applyInteractionVisibility(interactionGroupRef.current, vis);
		// Engines always build the neutral vent hardware; the analysis colours
		// are a view-layer material swap (issue 123).
		applyVentilationDiagramMode(result.saunaGroup, vis.ventilation);
		appliedVisibilityRef.current = vis;

		loadPremiumAssets(
			result,
			loadVersion,
			premiumLoadVersionRef,
			saunaGroupRef,
			onPremiumAssetLoaded,
		);
		attachSlotAssets(result.saunaGroup);
		// Swap glass before collecting heater targets so the emissive
		// `-door-glass` target list points at the live fire-glass variant.
		applyGlassQualityMode(result.saunaGroup, physicalGlassEnabledRef.current);
		applyWoodSurfaceQualityMode(
			result.saunaGroup,
			physicalWoodSurfacesEnabledRef.current,
		);
		refreshHeaterAnimationTargets(result.saunaGroup);

		if (foundationProgressRef.current === undefined && shouldPlayRevealAnimation({ isFirstBuild })) {
			// Assembly-order stagger: foundation rises first, exterior
			// trim last. Entries carry the final visibility so toggled-
			// off subsystems are never flashed on.
			const revealEntries: RevealGroupEntry[] = [
				{ object: result.foundationGroup, finalVisible: vis.foundation },
				{ object: result.deckGroup, finalVisible: vis.deck },
				{ object: result.framingGroup, finalVisible: vis.framing },
				{ object: result.panelGroup, finalVisible: vis.panel },
				{ object: result.roofGroup, finalVisible: vis.roof },
				{ object: result.interiorGroup, finalVisible: vis.interior },
				{ object: result.exteriorGroup, finalVisible: vis.exterior },
			].map((entry) => ({ ...entry, baseY: entry.object.position.y }));
			cancelRevealRef.current = playRevealAnimation(
				revealEntries,
				invalidate,
				notifySceneChanged,
			);
		} else {
			notifySceneChanged();
		}

		prevBuildInputRef.current = currentBuildInput;
		prevEngineRef.current = currentEngine;
		invalidate();
	}, [
		resolveParent,
		invalidate,
		notifySceneChanged,
		refreshHeaterAnimationTargets,
		onPremiumAssetLoaded,
		attachSlotAssets,
	]);

	const selectiveRebuild = useCallback(() => {
		const currentBuildInput = buildInputRef.current;
		const currentEngine = engineRef.current;

		const saunaGroup = saunaGroupRef.current;
		const prev = prevBuildInputRef.current;

		if (!saunaGroup || !prev || prevEngineRef.current !== currentEngine) {
			fullRebuild();
			return;
		}

		const dirtyFlags = computeDirtyFlags(prev, currentBuildInput, currentEngine === getEngine("tvarum"));
		if (dirtyFlags.size === 0) return;

		const isFullDirty = dirtyFlags.size === ALL_DIRTY.size;
		if (
			isFullDirty ||
			!currentEngine.buildSubsystem ||
			!currentEngine.createBuildContext
		) {
			fullRebuild();
			return;
		}

		const ctx = currentEngine.createBuildContext(currentBuildInput);
		const vis = visibilityRef.current;

		for (const flag of dirtyFlags) {
			const groupKey = flagToGroupKey[flag];
			const visKey = flagToVisKey[flag];
			const oldGroup = groupRefs.current[groupKey];

			const newGroup = currentEngine.buildSubsystem(
				flag,
				currentBuildInput,
				ctx,
			);
			newGroup.visible = vis[visKey];
			applyDividerVisibility(newGroup, vis.dividerWall !== false);

			if (flag === DirtyFlag.Framing) {
				syncOpeningGuides(
					newGroup,
					currentBuildInput,
					showOpeningGuidesRef.current,
				);
			}

			if (oldGroup) {
				disposeGroupGeometries(oldGroup);
				saunaGroup.remove(oldGroup);
			}
			saunaGroup.add(newGroup);
			groupRefs.current[groupKey] = newGroup;

			if (groupKey !== "foundation") {
				const frontGroup = getFrontVisibilityGroup(
					newGroup,
					groupKey as FrontVisibilityLayer,
				);
				if (frontGroup) {
					frontVisibilityRefs.current[groupKey as FrontVisibilityLayer] =
						frontGroup;
					frontGroup.visible = vis.frontWall;
				} else {
					delete frontVisibilityRefs.current[groupKey as FrontVisibilityLayer];
				}
			}

			if (groupKey === "panel") {
				applyCeilingFinishVisibility(newGroup, vis);
			}

			// A rebuilt interior brings a fresh ceiling-visibility subgroup,
			// which is born visible — re-apply `roof` to it or the batts come
			// back over a scene the user has TAK toggled off in.
			if (groupKey === "interior") {
				applyCeilingStackVisibility(newGroup, vis);
			}
		}

		if (interactionGroupRef.current) {
			disposeInteractionGroup(interactionGroupRef.current);
			saunaGroup.remove(interactionGroupRef.current);
		}
		const interactionGroup = createInteractionOverlays(currentBuildInput);
		saunaGroup.add(interactionGroup);
		interactionGroupRef.current = interactionGroup;
		applyInteractionVisibility(interactionGroup, vis);
		// Subsystem groups come back from the engine with the physical
		// glass and the neutral vent faces — re-apply both view-layer
		// material modes before re-collecting the heater emissive targets
		// (same ordering as fullRebuild).
		applyVentilationDiagramMode(saunaGroup, vis.ventilation);
		applyGlassQualityMode(saunaGroup, physicalGlassEnabledRef.current);
		applyWoodSurfaceQualityMode(
			saunaGroup,
			physicalWoodSurfacesEnabledRef.current,
		);
		refreshHeaterAnimationTargets(saunaGroup);
		attachSlotAssets(saunaGroup);

		prevBuildInputRef.current = currentBuildInput;
		appliedVisibilityRef.current = vis;
		notifySceneChanged();
		invalidate();
	}, [
		fullRebuild,
		flagToGroupKey,
		flagToVisKey,
		invalidate,
		notifySceneChanged,
		refreshHeaterAnimationTargets,
		attachSlotAssets,
	]);

	// Trigger rebuild when buildInput or engine changes.
	// Debounce with rAF to coalesce React StrictMode double-renders
	// and rapid state changes into a single GPU build.
	const rebuildFrameRef = useRef<number>(0);
	useEffect(() => {
		cancelAnimationFrame(rebuildFrameRef.current);
		rebuildFrameRef.current = requestAnimationFrame(() => {
			selectiveRebuild();
		});
		return () => cancelAnimationFrame(rebuildFrameRef.current);
	}, [buildInput, engine, selectiveRebuild]);

	// Update visibility without rebuild
	useEffect(() => {
		const sceneReady = saunaGroupRef.current !== null;
		const visibilityChanged = appliedVisibilityRef.current !== visibility;
		const stepViewChanged =
			previousStepViewTargetRef.current !== (stepView?.targetId ?? null);

		// Settle an in-flight reveal first so its final pose doesn't
		// overwrite the fresh visibility flags a few frames later.
		cancelRevealRef.current?.();
		cancelRevealRef.current = null;
		applySceneGroupVisibility(
			groupRefs.current,
			frontVisibilityRefs.current,
			visibility,
		);
		applyInteractionVisibility(interactionGroupRef.current, visibility);
		appliedVisibilityRef.current = visibility;
		let sceneChangeDeferred = false;
		if (
			stepView &&
			foundationProgressRef.current === undefined &&
			previousStepViewTargetRef.current !== stepView.targetId &&
			shouldPlayRevealAnimation({ isFirstBuild: true })
		) {
			const revealEntries: RevealGroupEntry[] = stepView.activeGroups
				.map((group) => groupRefs.current[group])
				.filter(
					(
						group,
					): group is NonNullable<
						(typeof groupRefs.current)[keyof typeof groupRefs.current]
					> => !!group,
				)
				.map((group) => ({
					object: group,
					finalVisible: group.visible,
					baseY: group.position.y,
				}));
			if (revealEntries.length > 0) {
				sceneChangeDeferred = true;
				cancelRevealRef.current = playRevealAnimation(
					revealEntries,
					invalidate,
					notifySceneChanged,
				);
			}
		}
		previousStepViewTargetRef.current = stepView?.targetId ?? null;
		if (
			sceneReady &&
			!sceneChangeDeferred &&
			(visibilityChanged || stepViewChanged)
		) {
			notifySceneChanged();
		}
		invalidate();
	}, [invalidate, notifySceneChanged, stepView, visibility]);

	useEffect(() => {
		const framingGroup = groupRefs.current.framing;
		if (!framingGroup) return;
		syncOpeningGuides(framingGroup, buildInputRef.current, showOpeningGuides);
		invalidate();
	}, [showOpeningGuides, invalidate]);

	// The ventilation layer recolours vents rather than hiding anything, so
	// it rides the live material-swap path, not the visibility path — it
	// must not re-traverse the scene on every unrelated layer toggle.
	useEffect(() => {
		const saunaGroup = saunaGroupRef.current;
		if (!saunaGroup) return;
		if (!applyVentilationDiagramMode(saunaGroup, visibility.ventilation)) {
			return;
		}
		invalidate();
	}, [visibility.ventilation, invalidate]);

	// Wood species and facade colour (issue 134) restyle the SHARED materials
	// rather than rebuilding geometry — a tint is not a different surface. The
	// apply must run before the first frame too, not only on change, so this
	// effect deliberately has no early-out on mount: `applyFinishSelection`
	// itself returns false when nothing moved.
	//
	// Panel DIRECTION is absent from the dependency list on purpose: it selects
	// a different material at build time, so `computeDirtyFlags` schedules a
	// panel/interior rebuild for it instead (see dirtyTracking's
	// GEOMETRY_AFFECTING_FINISH_KEYS).
	useEffect(() => {
		if (!applyFinishSelection(buildInput.finishes ?? DEFAULT_FINISHES)) {
			return;
		}
		invalidate();
	}, [buildInput.finishes, invalidate]);

	// Tier/cinematic changes arrive without a rebuild — swap live
	// material pairs in place. Glass changes also re-collect heater
	// targets so `-door-glass` emissive reset lands on the live variant.
	useEffect(() => {
		const saunaGroup = saunaGroupRef.current;
		if (!saunaGroup) return;
		const glassChanged = applyGlassQualityMode(
			saunaGroup,
			physicalGlassEnabled,
		);
		const woodChanged = applyWoodSurfaceQualityMode(
			saunaGroup,
			runtimeProfile.physicalWoodSurfacesEnabled,
		);
		if (!glassChanged && !woodChanged) {
			return;
		}
		if (glassChanged) {
			refreshHeaterAnimationTargets(saunaGroup);
		}
		invalidate();
	}, [
		physicalGlassEnabled,
		runtimeProfile.physicalWoodSurfacesEnabled,
		refreshHeaterAnimationTargets,
		invalidate,
	]);

	useEffect(() => {
		resetHeaterAnimationTargets(
			heaterAnimationTargetsRef.current,
			timeOfDay,
			runtimeProfile,
		);
		invalidate();
	}, [runtimeProfile, timeOfDay, invalidate]);

	useFrame((state) => {
		const assembly = assemblyRef.current;
		const root = groupRefs.current.foundation;
		const progress = foundationProgress ?? 1;
		const enabled = foundationProgress !== undefined;
		const targetsChanged = assembly.root !== root || assembly.enabled !== enabled;
		if (targetsChanged || assembly.progress !== progress || assembly.reducedMotion !== assemblyReducedMotion) {
			if (targetsChanged) {
				applyFoundationAssembly(assembly.parts, 1);
				assembly.parts = root && enabled ? collectFoundationAssembly(root) : [];
				assembly.root = root;
			}
			applyFoundationAssembly(assembly.parts, progress, assemblyReducedMotion);
			assembly.progress = progress;
			assembly.enabled = enabled;
			assembly.reducedMotion = assemblyReducedMotion;
		}

		const targets = heaterAnimationTargetsRef.current;
		if (
			animateHeaterAmbienceFrame(
				targets,
				runtimeProfile,
				timeOfDay,
				state.clock.getElapsedTime(),
			)
		) {
			invalidate();
		}
	});

	// This component manages scene objects imperatively via useEffect;
	// it renders nothing to the R3F tree directly.
	return null;
}
